import React from 'react';
import DSBreadcrumbs from 'Components/DesignSystem/Breadcrumbs/Breadcrumbs';
import { BreadcrumbItem as DSBreadcrumbItem } from 'types/DesignSystem/Breadcrumbs';

/**
 * This module's `BreadcrumbItem`, kept verbatim — `isActive` included.
 *
 * `BreadcrumbUtils` sets `isActive: true` on the current level, and both `ManageDomain`
 * and `ManageChannel` pass the result straight through. Narrowing this to the DS type
 * would break all three at once for no gain, so the flag stays in the contract and is
 * reconciled below.
 */
export type BreadcrumbItem = {
    label: string;
    href?: string;
    onClick?: () => void;
    isActive?: boolean;
}

export type BreadcrumbProps = {
    items: BreadcrumbItem[];
    /**
     * @deprecated Inert. `Breadcrumbs` owns its separator — an `aria-hidden`
     * `ChevronRight` outside the interactive element, where the old `>` text node sat
     * inside its own `<li>` and was announced as a list item. Kept so no call site
     * breaks; nothing passed it.
     */
    separator?: React.ReactNode;
    className?: string;
    /** @deprecated Inert — the DS component owns item typography (§ 52). */
    itemClassName?: string;
    /** @deprecated Inert — see `itemClassName`. */
    activeItemClassName?: string;
    /** @deprecated Inert — see `itemClassName`. */
    separatorClassName?: string;
    'data-testid'?: string;
}

/**
 * The Party-management breadcrumb on the design-system `Breadcrumbs` (§ 119).
 *
 * A thin adapter, deliberately: both parents (`ManageDomain`, `ManageChannel`) and
 * `BreadcrumbUtils` keep their existing shapes, and only the rendering changes.
 *
 * **`isActive` is reconciled, not forwarded.** The DS component decides the current
 * page by POSITION — the last item is always `aria-current="page"` and never
 * interactive, because a page does not link to itself. The old component used
 * `item.isActive || isLast`. Those agree for every builder in
 * `BreadcrumbUtils`: `createManageDomainBreadcrumbs` marks item 2 of 2,
 * `createManageChannelBreadcrumbs` marks item 3 of 3, and `createPageBreadcrumbs`
 * marks the last in both of its branches. The test for this file asserts that
 * agreement directly, so a future builder that marks a MIDDLE item active fails
 * loudly here instead of silently rendering the wrong level as current.
 *
 * What the swap fixes, all from the shared component:
 *
 * - **The separator was a list item.** `<li><span>&gt;</span></li>` put the glyph in
 *   the `ol` as a sibling of the real levels, so a screen reader announced a
 *   three-level trail as five items. The DS separator is `aria-hidden` and outside the
 *   interactive element.
 * - **No `aria-current`.** The active level was styled bold and nothing more, so
 *   assistive tech could not tell which level the user was on.
 * - **One shared, static testid.** Every clickable level rendered
 *   `data-testid="components-breadcrumb-button-1"` — on the Channel screen that is two
 *   elements with the same hook. Items now carry their own, templated from the label.
 */
export const Breadcrumb = ({ items, className = '', 'data-testid': dataTestId = 'pm-breadcrumb' }: BreadcrumbProps) => {
    if (!items || items.length === 0) {
        return null;
    }

    const dsItems: DSBreadcrumbItem[] = items.map((item) => ({
        label: item.label,
        href: item.href,
        /* The DS type takes a MouseEventHandler; this module's takes a bare thunk.
           Wrapped rather than cast, so an item without `onClick` stays a span. */
        onClick: item.onClick ? () => item.onClick?.() : undefined,
        'data-testid': `${dataTestId}-${slug(item.label)}`,
    }));

    return <DSBreadcrumbs items={dsItems} className={className} data-testid={dataTestId} />;
};

/**
 * Label → testid fragment. Party and domain names are user-supplied, so this has to
 * cope with spaces, punctuation and case ("ACME Test" → `acme-test`). Not unique by
 * construction — two levels could share a name — but the trail is at most three deep
 * and a party never carries its own domain's name.
 */
const slug = (label: string) =>
    label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
